<template>
  <div class="form-file-upload">
    <el-upload
      class="form-file-upload__uploader"
      action=""
      :show-file-list="showFileList"
      :file-list="fileList"
      :limit="limit"
      :disabled="disabled || uploading"
      :accept="accept"
      :http-request="uploadRequest"
      :before-upload="beforeUpload"
      :on-exceed="handleExceed"
      :on-remove="handleRemove"
      :on-success="handleUploadSuccess"
    >
      <span v-if="canUpload" class="form-file-upload__btn">
        <i class="el-icon-upload2"></i>
        {{ uploading ? "正在上传" : buttonText }}
      </span>
    </el-upload>
    <p v-if="tip" class="form-file-upload__tip">{{ tip }}</p>
  </div>
</template>

<script>
import { toolsts } from "@/api/lx";
import { guid } from "@/utils/other.js";
import ObsClient from "esdk-obs-browserjs";

export default {
  name: "FormFileUpload",
  props: {
    url: {
      type: [String, Array],
      default: "",
    },
    maxSize: {
      type: Number,
      default: 5,
    },
    accept: {
      type: String,
      default: ".pdf,application/pdf",
    },
    limit: {
      type: [String, Number],
      default: 1,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    buttonText: {
      type: String,
      default: "上传附件",
    },
    tip: {
      type: String,
      default: "",
    },
    showFileList: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      fileList: [],
      uploading: false,
    };
  },
  computed: {
    isSingle() {
      return Number(this.limit) === 1;
    },
    canUpload() {
      return this.fileList.length < Number(this.limit);
    },
  },
  watch: {
    url: {
      immediate: true,
      handler(val) {
        this.syncFileListFromUrl(val);
      },
    },
  },
  methods: {
    syncFileListFromUrl(val) {
      if (this.isSingle) {
        this.fileList = val
          ? [{ name: this.getFileName(val), url: val }]
          : [];
        return;
      }
      const urls = Array.isArray(val) ? val : [];
      this.fileList = urls.map((item) => {
        if (typeof item === "string") {
          return { name: this.getFileName(item), url: item };
        }
        return {
          name: item.name || this.getFileName(item.url),
          url: item.url,
        };
      });
    },
    getFileName(url) {
      if (!url || typeof url !== "string") return "附件";
      const parts = url.split("/");
      const fullName = decodeURIComponent(parts[parts.length - 1] || "附件");
      const idx = fullName.indexOf("_");
      return idx > -1 ? fullName.substring(idx + 1) : fullName;
    },
    isAcceptedFile(file) {
      const ext = file.name
        .substring(file.name.lastIndexOf(".") + 1)
        .toLowerCase();
      const mime = (file.type || "").toLowerCase();
      const acceptList = this.accept
        .split(",")
        .map((item) => item.trim().toLowerCase())
        .filter(Boolean);

      return acceptList.some((item) => {
        if (item.startsWith(".")) {
          return ext === item.slice(1);
        }
        if (item.includes("/")) {
          return mime === item;
        }
        return ext === item;
      });
    },
    beforeUpload(file) {
      const isAccepted = this.isAcceptedFile(file);
      const isLtMaxSize = file.size / 1024 / 1024 < this.maxSize;

      if (!isAccepted) {
        this.$message.error("上传格式不正确");
        return false;
      }
      if (!isLtMaxSize) {
        this.$message.error(`文件大小不能超过 ${this.maxSize}MB`);
        return false;
      }

      this.uploading = true;
      return true;
    },
    uploadRequest(option) {
      const fileNames = `backstage/${guid()}_${option.file.name}`;

      toolsts()
        .then((res) => {
          if (res.code !== 200) {
            this.$message.error(res.msg || "获取上传凭证失败");
            this.uploading = false;
            option.onError(new Error(res.msg || "获取上传凭证失败"));
            return;
          }

          const { bucket, endpoint, ak, sk } = res.data;
          const obsClient = new ObsClient({
            access_key_id: ak,
            secret_access_key: sk,
            server: `https://${endpoint}`,
            x_obs_acl: "public-read",
          });

          obsClient.putObject(
            {
              Bucket: bucket,
              Key: fileNames,
              Body: option.file,
              ACL: "public-read",
            },
            (err, result) => {
              if (err) {
                this.$message.error("华为云上传失败");
                this.uploading = false;
                option.onError(err);
                return;
              }

              if (result.CommonMsg.Status === 200) {
                const fileUrl = `https://${bucket}.${endpoint}/${fileNames}`;
                option.onSuccess(fileUrl);
              } else {
                this.$message.error("华为云上传失败");
                this.uploading = false;
                option.onError(new Error("华为云上传失败"));
              }
            }
          );
        })
        .catch(() => {
          this.$message.error("华为云上传失败");
          this.uploading = false;
          option.onError(new Error("华为云上传失败"));
        });
    },
    handleUploadSuccess(url, file) {
      this.uploading = false;
      const fileItem = {
        name: file.name,
        url,
      };

      if (this.isSingle) {
        this.fileList = [fileItem];
        this.$emit("upload", url);
        return;
      }

      const nextUrls = [...this.getUrlValue(), url];
      this.fileList = [...this.fileList, fileItem];
      this.$emit("upload", nextUrls);
    },
    handleRemove(file) {
      if (this.isSingle) {
        this.fileList = [];
        this.$emit("upload", "");
        return;
      }

      const removeUrl = file.url;
      const nextUrls = this.getUrlValue().filter((item) => item !== removeUrl);
      this.fileList = this.fileList.filter((item) => item.url !== removeUrl);
      this.$emit("upload", nextUrls);
    },
    getUrlValue() {
      if (this.isSingle) {
        return this.fileList[0] ? this.fileList[0].url : "";
      }
      return this.fileList.map((item) => item.url);
    },
    handleExceed() {
      this.$message.error(`超出最大允许上传数量 (${this.limit})`);
      this.uploading = false;
    },
  },
};
</script>

<style lang="less" scoped>
.form-file-upload__btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  color: #409eff;
  font-size: 14px;
  cursor: pointer;

  i {
    font-size: 18px;
  }

  &:hover {
    opacity: 0.85;
  }
}

.form-file-upload__tip {
  margin: 4px 0 0;
  font-size: 10px;
  color: #9ca3af;
  line-height: 1.4;
}

/deep/ .form-file-upload__uploader .el-upload-list {
  margin-top: 4px;
}
</style>
